import React, { useState } from "react";

import { Switch } from "antd";

import { Modal } from "shared/components/Modal";
import useModal from "shared/hooks/useModal";

import { GreenSwitch, VeroGoOverrideModalContext } from "./shared";

const StateRow = ({
  label,
  labelColor,
  state,
  relationship,
}: {
  label: string;
  labelColor: string;
  state: "on" | "off";
  relationship: "inherited" | "custom";
}) => {
  const isOn = state === "on";
  const isInherited = relationship === "inherited";

  const SwitchComponent = isOn ? GreenSwitch : Switch;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.5px",
          textTransform: "uppercase" as const,
          color: labelColor,
          width: 48,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span style={{ opacity: 1, pointerEvents: "none" as const }}>
        <SwitchComponent checked={isOn} disabled size="small" />
      </span>
      <span
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: "#111827",
          flex: 1,
        }}
      >
        Vero Go is {isOn ? "On" : "Off"}
      </span>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          padding: "2px 10px",
          borderRadius: 999,
          background: isInherited ? "#F3F4F6" : "#f5f3ff",
          color: isInherited ? "#6B7280" : "#7c3aed",
          border: `1px solid ${isInherited ? "#E5E7EB" : "#c4b5fd"}`,
        }}
      >
        {isInherited ? "Inherited" : "Custom"}
      </span>
    </div>
  );
};

const DownArrow = () => (
  <div style={{ textAlign: "center", margin: "10px 0" }}>
    <svg
      width="20"
      height="20"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 3V13M8 13L4 9M8 13L12 9"
        stroke="#9CA3AF"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const InfoIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0, marginTop: 1 }}
  >
    <circle cx="8" cy="8" r="7" fill="#D97706" opacity="0.15" />
    <circle cx="8" cy="8" r="7" stroke="#D97706" strokeWidth="1" />
    <path
      d="M8 7V11"
      stroke="#D97706"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="8" cy="5" r="0.75" fill="#D97706" />
  </svg>
);

const VeroGoOverrideModal = () => {
  const [submitting, setSubmitting] = useState(false);
  const { currentModalContext, selfApi } = useModal();
  const {
    title,
    subtitle,
    nowState,
    nowRelationship,
    afterState,
    afterRelationship,
    infoText,
    primaryButtonLabel,
    secondaryButtonLabel,
    primaryValue,
    secondaryValue,
  } = currentModalContext as VeroGoOverrideModalContext;

  return (
    <Modal
      title={title}
      centered
      showSubmitButton
      submitButtonLabel={primaryButtonLabel}
      submit={async () => {
        try {
          setSubmitting(true);
        } finally {
          setSubmitting(false);
        }
        selfApi.setClosingValue(primaryValue);
      }}
      submitting={submitting}
      showCancelLink
      cancelLinkLabel={secondaryButtonLabel}
      onCancelLinkClick={() => {
        selfApi.setClosingValue(secondaryValue);
        selfApi.closeDialog();
      }}
    >
      <Modal.Body style={{ paddingTop: 16 }}>
        <p
          style={{
            margin: "0 0 12px",
            fontSize: 14,
            color: "#374151",
          }}
        >
          <strong>{subtitle.split(" in ")[0]}</strong>
          {subtitle.includes(" in ") && (
            <span style={{ color: "#6B7280" }}>
              {" "}
              in {subtitle.split(" in ").slice(1).join(" in ")}
            </span>
          )}
        </p>
        <p
          style={{
            margin: "0 0 16px",
            fontSize: 13,
            lineHeight: 1.45,
            color: "#6B7280",
          }}
        >
          Vero Go screens applicants using identity verification and
          document-based income verification only.
        </p>
        <div
          style={{
            border: "1px solid #E5E7EB",
            borderRadius: 12,
            padding: "20px 24px",
            marginBottom: 16,
          }}
        >
          <StateRow
            label="NOW"
            labelColor="#6B7280"
            state={nowState}
            relationship={nowRelationship}
          />
          <DownArrow />
          <StateRow
            label="AFTER"
            labelColor="#7c3aed"
            state={afterState}
            relationship={afterRelationship}
          />
        </div>

        <div
          style={{
            background: "#FFFBEB",
            border: "1px solid #FDE68A",
            borderRadius: 10,
            padding: "12px 16px",
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          <InfoIcon />
          <span
            style={{
              fontSize: 13,
              lineHeight: 1.45,
              color: "#92400E",
            }}
          >
            {infoText}
          </span>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default VeroGoOverrideModal;
